import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { iMap } from '../../../../components/type';
import type { SelectedCollectCollection } from '../../../../utils/analysisContextLinks';

export function useFileUpload() {
    const { t } = useTranslation();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedMaps, setSelectedMaps] = useState<iMap[]>([]);
    const [selectedCollections, setSelectedCollections] = useState<SelectedCollectCollection[]>([]);
    const [fileError, setFileError] = useState<string | null>(null);

    const MAX_FILE_SIZE = 10 * 1024 * 1024;

    const handleFileSelect = (file: File | null) => {
        setFileError(null);

        if (!file) {
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setFileError(t("navigation.fileSize"));
            return;
        }

        setSelectedFiles(prev => {
            const isDuplicate = prev.some(f => f.name === file.name && f.size === file.size);
            if (isDuplicate) {
                setFileError(t("navigation.fileAlreadySelected") || "Ce fichier est déjà sélectionné");
                return prev;
            }
            return [...prev, file];
        });
    };

    const handleMapSelect = (maps: iMap[] | null) => {
        setSelectedMaps(maps ?? []);
    };

    const handleCollectionSelect = (collections: SelectedCollectCollection[] | null) => {
        setSelectedCollections(collections ?? []);
    };

    const removeFile = (fileIndex?: number) => {
        if (fileIndex !== undefined) {
            setSelectedFiles(prev => prev.filter((_, index) => index !== fileIndex));
        } else {
            setSelectedFiles([]);
        }
        setFileError(null);
    };

    const removeMap = (mapId: string) => {
        setSelectedMaps(prev => prev.filter(map => map._id !== mapId));
    };

    const removeCollection = (collectionId: string) => {
        setSelectedCollections(prev => prev.filter(c => c.id !== collectionId));
    };

    const clearSelection = () => {
        setSelectedFiles([]);
        setSelectedMaps([]);
        setSelectedCollections([]);
        setFileError(null);
    };

    const selectedFile = selectedFiles.length > 0 ? selectedFiles[0] : null;

    return {
        selectedFiles,
        selectedFile,
        selectedMaps,
        selectedMapIds: selectedMaps.map(m => m._id),
        selectedCollections,
        selectedCollectionIds: selectedCollections.map(c => c.id),
        fileError,
        handleFileSelect,
        handleMapSelect,
        handleCollectionSelect,
        removeFile,
        removeMap,
        removeCollection,
        clearSelection
    };
}
